import React from "react";
import {FormImageRequirement, FormImageType, FormSubmissionFileType, SimpleSelectOption} from "../../api/ApiTypes.ts";
import {ExampleStaticFieldModal} from "./modals/ExampleStaticFieldModal.tsx";
import {OriginalComparableFinderModal} from "./modals/OriginalComparableFinderModal.tsx";
import {DefaultScope} from "./DefaultScope.tsx";
import {ConfirmEffectiveDateModal} from "./modals/ConfirmEffectiveDateModal.tsx";
import {ClearEffectiveDateModal} from "./modals/ClearEffectiveDateModal.tsx";
import {ControllerFieldState, ControllerRenderProps, FieldValues} from "react-hook-form";


export enum FormType {
    PANEL = 'panel',
    TEXTFIELD = 'textfield',
    TEXTAREA = 'textarea',
    NUMBER = 'number',
    DATETIME = 'datetime',
    CURRENCY = 'currency',
    CHECKBOX = 'checkbox',
    SELECT = 'select',
    DATAGRID = 'datagrid',
    RADIO = 'radio',
    SIMPLE_DATE = "simpledate",
    LAYOUT_GRID = "layoutgrid",
    STATIC_FIELD = "staticfield",
    API_COMPONENT = "apicomponent",
    FILE_UPLOAD = "fileupload",
    CONFIGURABLE_COMPS_SELECT = "configurablecompsselect",
    DERIVED = "derived",
    IMAGE_UPLOAD = "imageupload",
    EXPANDED_IMAGE_UPLOAD = "expandedimageupload",
    CUSTOM_VALIDATOR = "customvalidator",
    LICENSE_BUTTONS = "licensebuttons",
    SPACER = 'spacer',
    AERIAL_MAP = 'aerialmap',
    DUPLICATED = 'duplicated',
    COMP_SUMMARY = 'compsummary',
    QC_CHECKLIST = "qcchecklist",
}


export interface ComponentMetaData<T extends BaseFormComponent> {
    reactComponent: React.FC<FormComponentProps<T>>;
    isNested: boolean;
}


export interface FormSchema {
    components: FormComponent[];
}

export interface BaseFormComponentProps {
}

//every form component props
export interface FormComponentProps<T extends BaseFormComponent> extends BaseFormComponentProps {
    component: T;
}

//every form component has these properties
export interface BaseFormComponent {
    type: FormType;
    fieldKey: string;
    mapTo?: string;
    label: string;
    defaultValue?: DefaultValue;
    description?: string;
    validationRules?: ValidationRule[];
    conditional?: ConditionalRule;
    width?: number
    suffix?: string;

    //we will add these properties after we get it from the backend!!
    componentId?: string;
    compactUI?: boolean;
    onFieldBlur?: FrontEndAction;
    infoConfig?: InfoConfig;
    fieldsToTriggerOnBlur?: ListFieldKeys[];
}

export interface InfoConfig {
    title?: string;
    content?: string;
}

export interface FrontEndAction {
    type: FrontEndActionType;
    job: FrontEndActionJob;
    params?: any;
}

export enum FrontEndActionJob {
    ORIGINAL_COMPARABLE_DATA_SEARCH = "ORIGINAL_COMPARABLE_DATA_SEARCH",
    COMP_SUBJECT_PROXIMITY = "COMP_SUBJECT_PROXIMITY",
}

export enum FrontEndActionType {
    API_CALL = 'API_CALL',
}

export interface DefaultValue {
    value?: any;
    metaDataKey?: string;
}

// Conditional display rules
export interface ConditionalRule {
    dontClearFields?: boolean;
    warningMessage?: string;
    when?: string;
    eq?: any;

    // New properties for enhanced conditions
    notEq?: any;
    isEmpty?: boolean;
    notEmpty?: boolean;

    // Number comparisons
    greaterThan?: number;
    lessThan?: number;
    greaterThanOrEqual?: number;
    lessThanOrEqual?: number;

    // String operations
    contains?: string;
    startsWith?: string;
    endsWith?: string;

    // Array operations
    includesAny?: any[];
    doesntIncludeAny?: any[];
    includesAll?: any[];

    // Logical operators
    and?: ConditionalRule[];
    or?: ConditionalRule[];
    not?: ConditionalRule;
}

export interface PanelStaticHeader {
    fieldKey: string;
    display: string;
    formatAs: FormatAs;
}

export interface PanelComponent extends BaseFormComponent {
    type: FormType.PANEL;
    showLabel?: boolean;
    components: FormComponent[];
    staticHeaders? : PanelStaticHeader[];
}

export interface LayoutGridComponent extends BaseFormComponent {
    type: FormType.LAYOUT_GRID;
    showLabel?: boolean;
    components: FormComponent[];
}

export interface TextFieldComponent extends BaseFormComponent {
    type: FormType.TEXTFIELD;
    placeHolder?: string;
}

export interface DateTimeComponent extends BaseFormComponent {
    type: FormType.DATETIME;
}

export interface SimpleDateComponent extends BaseFormComponent {
    type: FormType.SIMPLE_DATE;
}

export interface CurrencyComponent extends BaseFormComponent {
    type: FormType.CURRENCY;
}

export interface APIComponent extends BaseFormComponent {
    type: FormType.API_COMPONENT;
    callType: APIComponentCallType;
    onChangeFieldKey: string;
}

export interface SpacerComponent extends BaseFormComponent {
    type: FormType.SPACER;
    size: number;
}

export interface AerialComponent extends BaseFormComponent {
    type: FormType.AERIAL_MAP
}

export interface DuplicatedComponent extends BaseFormComponent {
    type: FormType.DUPLICATED;
    duplicated: BaseFormComponent;
}

export interface CompSummaryComponent extends BaseFormComponent {
    type: FormType.COMP_SUMMARY;
}



export interface CustomValidatorComponent extends BaseFormComponent {
    type: FormType.CUSTOM_VALIDATOR;
    listFieldKeys: ListFieldKeys[];
    customValidatorType: string;
    rules?: string[]
}

export interface QCChecklistComponent extends BaseFormComponent {
    type: FormType.QC_CHECKLIST;
    checklist: string;
    rules: string[];
}

export enum APIComponentCallType {
    BASIC_AVM = "BASIC_AVM"
}


export interface CheckboxComponent extends BaseFormComponent {
    type: FormType.CHECKBOX;
}


export enum DataSource {
    PROVIDED = 'provided',
    API_TYPE = 'apitype',
}

export interface FileUploadComponent extends BaseFormComponent {
    type: FormType.FILE_UPLOAD;
    allowedFileTypes?: string[]
    fileType: FormSubmissionFileType
}

export interface ImageUploadComponent extends BaseFormComponent {
    type: FormType.IMAGE_UPLOAD,
    requiredType: FormImageType,
}

export interface ExpandedImageUploadComponent extends BaseFormComponent {
    type: FormType.EXPANDED_IMAGE_UPLOAD,
    requiredTypes?: FormImageRequirement[],
    optionalTypes?: FormImageType[],
    showsRepair?: boolean,
}

export interface SelectComponent extends BaseFormComponent {
    type: FormType.SELECT;
    dataSource: DataSource;
    provided?: SimpleSelectOption[];
    apiType?: string;
}

export interface DataGridComponent extends BaseFormComponent {
    type: FormType.DATAGRID;
    components: FormComponent[];
    useColumnLayout?: boolean;
    headerConfig?: DataGridHeaderConfig;
    defaultAmount?: number;
    itemName?: string;
}

export interface DataGridHeaderConfig {
    components: FormComponent[];
    label?: string;
}

export interface TextAreaComponent extends BaseFormComponent {
    type: FormType.TEXTAREA;
    placeHolder?: string;
}

export interface NumberComponent extends BaseFormComponent {
    type: FormType.NUMBER;
    formatNumberAs?: FormatNumberAs;
}

export interface RadioOption {
    label: string;
    value: string;
}

export interface RadioComponent extends BaseFormComponent {
    type: FormType.RADIO;
    options: RadioOption[];
}

export interface LicenseButtonsComponent extends BaseFormComponent {
    type: FormType.LICENSE_BUTTONS;
}


export interface ConfigurableCompsSelectComponent extends BaseFormComponent {
    type: FormType.CONFIGURABLE_COMPS_SELECT;
    components: FormComponent[];
    subType: ConfigurableCompsSelectSubType;
    compsDataGridInfos: CompsDataGridInfo[];
    effectiveDateFieldKey?: string;
}

export interface CompsDataGridInfo {
    dataGridFieldKey: string,
    prefixForApiPopulation: string,
    requiredType: CompSelectType
}

export enum CompSelectType {
    ANY = "ANY",
    ACTIVE = "ACTIVE",
    SOLD = "SOLD",
}

export enum ConfigurableCompsSelectSubType {
    LISTING_AND_SOLD = "LISTING_AND_SOLD",
    ANY = "ANY",
    REPAIRED_OR_NOT = "REPAIRED_OR_NOT",
}

export interface StaticFieldComponent extends BaseFormComponent {
    watchFieldKey?: string;
    metaDataKey?: string;
    value?: any;
    placeHolder?: any;
    formatAs?: FormatAs;
    match?: any;
}

export interface DerivedComponent extends BaseFormComponent {
    listFieldKeys: ListFieldKeys[];
    calculationRule: RuleType;
    formatNumberAs?: FormatNumberAs;
    provided?: SimpleSelectOption[];
    forceValueWhen?: string[];
    forcedValue?: any;
}



export enum RuleType {
    SUM_ALL = "SUM_ALL",
    SUM_RECOMMENDED_REPAIRS = "SUM_RECOMMENDED_REPAIRS",
    MOST_COMPARABLE = "MOST_COMPARABLE",
    ADJUSTMENT = "ADJUSTMENT",
    COMBINED_ADJUSTMENT = "COMBINED_ADJUSTMENT",
    BASEMENT_ADJUSTMENT = "BASEMENT_ADJUSTMENT",
    SUM_ONE = "SUM_ONE",
    PERCENT_OF = "PERCENT_OF",
    SUM_ABS_ONE = "SUM_ABS_ONE",
    LOWEST = "LOWEST",
    HIGHEST = "HIGHEST",
    MOST_RECENT = "MOST_RECENT",
    NINETY_DAY_AS_REPAIRED = "NINETY_DAY_AS_REPAIRED",
    SOMETIMES_SELECT = "SOMETIMES_SELECT",
    SOMETIMES_NUMBER = "SOMETIMES_NUMBER",
    MULTIPLY_PERCENT = "MULTIPLY_PERCENT",
    GARAGE_ADJUSTMENT = "GARAGE_ADJUSTMENT",
    DAYS_DIFFERENCE = "DAYS_DIFFERENCE"
}

export interface ListFieldKeys {
    fieldKey: string,
    dataGrid?: string,
    meta: KeyGridMeta
}

export enum KeyGridMeta {
    NUMERATOR = "NUMERATOR",
    DENOMINATOR = "DENOMINATOR",
    RECOMMENDED = "RECOMMENDED",
    REPAIR_COST = "REPAIR_COST",
    PRICE = "PRICE",
    DATE = "DATE",
    PERCENTAGE = "PERCENTAGE",
    NUMBER = "NUMBER",
    ROI = "ROI",
    TOTAL_REPAIRS = "TOTAL_REPAIRS",
    ADJUSTMENT_MULTIPLIER = "ADJUSTMENT_MULTIPLIER",
    COMPARABLE_AMOUNT = "COMPARABLE_AMOUNT",
    SUBJECT_AMOUNT = "SUBJECT_AMOUNT",
    ADJUSTMENT_MULTIPLIER_A = "ADJUSTMENT_MULTIPLIER_A",
    ADJUSTMENT_MULTIPLIER_B = "ADJUSTMENT_MULTIPLIER_B",
    SUBJECT_AMOUNT_A = "SUBJECT_AMOUNT_A",
    SUBJECT_AMOUNT_B = "SUBJECT_AMOUNT_B",
    COMPARABLE_AMOUNT_A = "COMPARABLE_AMOUNT_A",
    COMPARABLE_AMOUNT_B = "COMPARABLE_AMOUNT_B",
    SUBJECT_TYPE = "SUBJECT_TYPE",
    COMPARABLE_TYPE = "COMPARABLE_TYPE",
    LATER_DATE = "LATER_DATE",
    EARLIER_DATE = "EARLIER_DATE",
    WATCHED_SELECT_FIELD = "WATCHED_SELECT_FIELD"
}

export enum FormatAs {
    LINK = "LINK",
    CURRENCY ="CURRENCY",
    PERCENTAGE = "PERCENTAGE",
    NONE = "NONE",
    MODAL = "MODAL",
    COMPONENT = "COMPONENT",
    DATE = "DATE",
    SELECT_DESCRIPTION = "SELECT_DESCRIPTION",
    SPACER = "SPACER",
    LABEL = "LABEL",
    CHECKBOX = "CHECKBOX",
    IMAGE = "IMAGE",
    REPAIR_IMAGES = "REPAIR_IMAGES"
}

export function getDontRegister(component: StaticFieldComponent): boolean {
    if (component.formatAs && (component.formatAs === FormatAs.LABEL || component.formatAs === FormatAs.SPACER)) {
        return true;
    }
    return false;
}

export enum FormatNumberAs {
    PERCENT = "PERCENT",
    NONE = "NONE",
    INTEGER = "INTEGER",
    DOUBLE = "DOUBLE",
    CURRENCY_DOUBLE = "CURRENCY_DOUBLE",
    CURRENCY_INTEGER = "CURRENCY_INTEGER",
}

export type FormComponent =
    | TextFieldComponent
    | TextAreaComponent
    | NumberComponent
    | CurrencyComponent
    | SelectComponent
    | CheckboxComponent
    | RadioComponent
    | DateTimeComponent
    | SimpleDateComponent
    | DataGridComponent
    | PanelComponent
    | LayoutGridComponent
    | StaticFieldComponent
    | APIComponent
    | FileUploadComponent
    | ConfigurableCompsSelectComponent
    | DerivedComponent;

export type FormValues = Record<string, any>;

export enum ValidationRuleType {
    REQUIRED = 'REQUIRED',
    MIN = 'MIN',
    MAX = 'MAX',
    PATTERN = 'PATTERN',
    NOT_EQUALS_META = 'NOT_EQUALS_META',
    NOT_EQUALS_FIELD = 'NOT_EQUALS_FIELD',
    EQUALS_FIELD = 'EQUALS_FIELD',
    RISK_RATING_VALUE = 'RISK_RATING_VALUE',
    RISK_RATING_VARIANCE = 'RISK_RATING_VARIANCE',
    RISK_RATING_SELECT_COMPARABLES = 'RISK_RATING_SELECT_COMPARABLES',
    RISK_RATING_CONCLUSION = 'RISK_RATING_CONCLUSION',
    SUBJECT_PROPERTY_MIXED_USE_OR_COMMERCIAL = 'SUBJECT_PROPERTY_MIXED_USE_OR_COMMERCIAL',
    SUBJECT_PROPERTY_INSPECTIONS_DATE_VALID = 'SUBJECT_PROPERTY_INSPECTIONS_DATE_VALID',
    PERCENT_OCCUPIED_VACANCY = 'PERCENT_OCCUPIED_VACANCY',
    NEIGHBORHOOD_PRICE_RANGE = 'NEIGHBORHOOD_PRICE_RANGE',
    NAUGHTY_WORDS = "NAUGHTY_WORDS",
    LESS_THAN = "LESS_THAN",
    SUBJECT_CONDITION = "SUBJECT_CONDITION",
    TITLE_LEGAL_ISSUES = "TITLE_LEGAL_ISSUES",
    HEALTH_SAFETY_ISSUES = "HEALTH_SAFETY_ISSUES",
    ENVIRONMENTAL_ISSUES = "ENVIRONMENTAL_ISSUES",
    IMMEDIATE_REPAIRS_REQUIRED = "IMMEDIATE_REPAIRS_REQUIRED",
    AS_IS_LIST_PRICE = "AS_IS_LIST_PRICE",
    AS_IS_LIST_PRICE_WARNING = "AS_IS_LIST_PRICE_WARNING",
    AS_IS_SALE_PRICE = "AS_IS_SALE_PRICE",
    AS_IS_SALE_PRICE_WARNING = "AS_IS_SALE_PRICE_WARNING",
    AS_REPAIRED_LIST_PRICE = "AS_REPAIRED_LIST_PRICE",
    AS_REPAIRED_LIST_PRICE_WARNING = "AS_REPAIRED_LIST_PRICE_WARNING",
    AS_REPAIRED_SALE_PRICE = "AS_REPAIRED_SALE_PRICE",
    GREATER_THAN_OR_EQUAL = "GREATER_THAN_OR_EQUAL",
    BEDROOM_ADJUSTMENT = "BEDROOM_ADJUSTMENT",
    GARAGE_ADJUSTMENT = "GARAGE_ADJUSTMENT",
    FULL_BATH_ADJUSTMENT = "FULL_BATH_ADJUSTMENT",
    HALF_BATH_ADJUSTMENT = "HALF_BATH_ADJUSTMENT",
    LIVING_AREA_ADJUSTMENT = "LIVING_AREA_ADJUSTMENT",
    BATH_ADJUSTMENT_VALIDATION = "BATH_ADJUSTMENT_VALIDATION"
}

export interface ValidationRule {
    type: ValidationRuleType;
    params?: any;
    message?: string;
    level?: ValidationRuleLevel;
}

export enum ValidationRuleLevel {
    ERROR = "ERROR",
    WARNING = "WARNING"
}

export enum CUSTOM_ERROR_TYPE {
    BACKEND = "BACKEND",//these are error from the backend (after submission), we add them on but dont require them to be fixed before submitting again
    MANUAL = "MANUAL", //these are errors that are manually added to the form, like a custom error message
    FACTORY = "FACTORY", //these are errors that are added by the validation factory
}

export interface CustomErrorInfo {
    type: CUSTOM_ERROR_TYPE;
    level: ValidationRuleLevel;
}

export interface StaticFieldModalConfig {
    type: STATIC_MODAL_TYPE;
    extraProps?: Record<string, any>;
}

export interface StaticPanelHeaderLinkConfig {
    href: string;
    display: string;
}

export interface BaseStaticFieldModalProps {
    fieldKey: string;
    isOpen: boolean;
    onClose: () => void;
    config: StaticFieldModalConfig
}

export enum STATIC_MODAL_TYPE {
    EXAMPLE = "EXAMPLE",
    ORIGINAL_MLS_DATA_FINDER = "ORIGINAL_MLS_DATA_FINDER",
    CONFIRM_EFFECTIVE_DATE = "CONFIRM_EFFECTIVE_DATE",
    CLEAR_EFFECTIVE_DATE = "CLEAR_EFFECTIVE_DATE",
}


export const STATIC_MODAL_LABEL: Record<STATIC_MODAL_TYPE, string> = {
    [STATIC_MODAL_TYPE.EXAMPLE]: "Example Modal",
    [STATIC_MODAL_TYPE.ORIGINAL_MLS_DATA_FINDER]: "Search MLS",
    [STATIC_MODAL_TYPE.CONFIRM_EFFECTIVE_DATE]: "Confirm",
    [STATIC_MODAL_TYPE.CLEAR_EFFECTIVE_DATE]: "Clear",
}
export const STATIC_MODAL_COMPONENTS: Record<STATIC_MODAL_TYPE, React.ComponentType<any>> = {
    [STATIC_MODAL_TYPE.EXAMPLE]: ExampleStaticFieldModal,
    [STATIC_MODAL_TYPE.ORIGINAL_MLS_DATA_FINDER]: OriginalComparableFinderModal,
    [STATIC_MODAL_TYPE.CONFIRM_EFFECTIVE_DATE]: ConfirmEffectiveDateModal,
    [STATIC_MODAL_TYPE.CLEAR_EFFECTIVE_DATE]: ClearEffectiveDateModal,
}

export enum STATIC_RENDER_TYPE {
    DEFAULT_SCOPE = "DEFAULT_SCOPE"
}

export const STATIC_RENDER_COMPONENTS: Record<STATIC_RENDER_TYPE, React.ComponentType<any>> = {
    [STATIC_RENDER_TYPE.DEFAULT_SCOPE]: DefaultScope
}

export type ParentController = {
    field: ControllerRenderProps<FieldValues, string>;     // includes ref, name, value, onChange, onBlur, disabled
    fieldState: ControllerFieldState;                      // { invalid,isTouched,isDirty,isValidating,error }
    // formState?: UseFormStateReturn<FieldValues>;           // optional: only pass if child needs it
};